// src/services/purchasingReport.js
import axios from 'axios';
import { URLEndpoints } from '../../constants/urlConstants';
import {
  createReportDefinition,
  deleteReportDefinition,
  getReportDefinition,
  listReportDefinitions,
  pinReportDefinition,
  publishReportDefinitionsInvalidation,
  subscribeReportDefinitionsInvalidation,
  updateReportDefinition,
} from '../reportDefinitions';

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;

// ────────────────────────────────────────────────────────────
// Cache (sessionStorage) – per-definition and per-list (by instance)
// ────────────────────────────────────────────────────────────
const TTL = 5 * 60 * 1000; // 5 minutes

const runsKey  = (defId) => `pr:runs:${defId}`;

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || !data) return null;
    if (Date.now() - ts > TTL) return null;
    return data;
  } catch {
    return null;
  }
}
function writeCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}
function invalidate(key) {
  try { sessionStorage.removeItem(key); } catch {}
}

// optional cross-tab bust
export const publishPurchasingReportInvalidation = publishReportDefinitionsInvalidation;
export const subscribePurchasingReportInvalidation = subscribeReportDefinitionsInvalidation;

// ────────────────────────────────────────────────────────────
/** Auth headers (match other services: Authorization: Bearer <token>) */
// ────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('accessToken');
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const ensureBearer = (t) => (t?.startsWith('Bearer ') ? t : `Bearer ${t}`);

// ────────────────────────────────────────────────────────────
// Definitions (CRUD)
// ────────────────────────────────────────────────────────────

// List report definitions (scoped by instance)
export async function listPurchasingReportDefinitions(instanceUniqueId, params = {}) {
  return listReportDefinitions(instanceUniqueId, {
    ...params,
    reportKey: 'purchasingDashboard',
  });
}

// Create a definition (always include instanceUniqueId in the body)
export async function createPurchasingReportDefinition(payload, instanceUniqueId) {
  const body = {
    ...(payload || {}),
    config_json: {
      ...(payload?.config_json || {}),
      reportKey: 'purchasingDashboard',
    },
  };
  return createReportDefinition(body, instanceUniqueId);
}

// Get one definition
export async function getPurchasingReportDefinition(id, instanceUniqueId) {
  return getReportDefinition(id, instanceUniqueId, { reportKey: 'purchasingDashboard' });
}

// Update a definition (always include instanceUniqueId in the body)
export async function updatePurchasingReportDefinition(id, payload, instanceUniqueId) {
  const body = {
    ...(payload || {}),
    config_json: {
      ...(payload?.config_json || {}),
      reportKey: 'purchasingDashboard',
    },
  };
  return updateReportDefinition(id, body, instanceUniqueId);
}

export async function pinPurchasingReportDefinition(id, { instanceUniqueId, pinned }) {
  return pinReportDefinition(id, {
    instanceUniqueId,
    reportKey: 'purchasingDashboard',
    pinned,
  });
}

// Soft delete a definition
export async function deletePurchasingReportDefinition(id, instanceUniqueId) {
  return deleteReportDefinition(id, instanceUniqueId, { reportKey: 'purchasingDashboard' });
}

// ────────────────────────────────────────────────────────────
/** Runs (queue + history) */
// ────────────────────────────────────────────────────────────

// Queue a run for a definition
export async function runPurchasingReportDefinition(id, instanceUniqueId, tokenOverride) {
  try {
    const headers = tokenOverride
      ? { Authorization: ensureBearer(tokenOverride) }
      : authHeaders();

    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}/run`,
      { instanceUniqueId },
      { headers }
    );
    invalidate(runsKey(id));
    return res?.data?.data; // ReportRun row
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

// List runs for a definition
export async function listPurchasingReportRuns(id, instanceUniqueId, params = {}) {
  try {
    const cacheId = runsKey(id);
    const cached = readCache(cacheId);
    const simple = !params.limit && !params.offset;
    if (cached && simple) return cached;

    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/${URLEndpoints.reportDefinitions}/${id}/${URLEndpoints.reportRuns}`,
      { params: { instanceUniqueId, ...params }, headers: authHeaders() }
    );
    const data = res?.data?.data || [];
    if (simple) writeCache(cacheId, data);
    return data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

// ────────────────────────────────────────────────────────────
// (Optional) Compatibility stubs your UI might still call
// ────────────────────────────────────────────────────────────

export async function searchPurchasingReportsCompat(params = {}) {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/search`,
      { params, headers: authHeaders() }
    );
    return res?.data; // likely { reports: [] } for now
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

export async function getReportNowCompat(body = {}) {
  try {
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchasingReport}/get-report`,
      body,
      { headers: authHeaders() }
    );
    return res?.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}
